/**
 * Accounts Hook
 * Jotai atoms and hooks for account management (super admin only)
 */

import { useAtom } from 'jotai';
import { atom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { accountService } from '@/services';
import { invalidateQueries } from '@/lib/query-client';

const accountsPaginationAtom = atom<{ page: number; limit: number }>({
  page: 1,
  limit: 10
});

const accountsFiltersAtom = atom<{
  search: string;
  shadowMode: 'all' | 'enabled' | 'disabled';
  v3: 'all' | 'enabled' | 'disabled';
}>({
  search: '',
  shadowMode: 'all',
  v3: 'all'
});

// Query atom
const accountsQueryAtom = atomWithQuery((get) => {
  const { page, limit } = get(accountsPaginationAtom);
  const { search, shadowMode, v3 } = get(accountsFiltersAtom);
  return {
    queryKey: ['accounts', page, limit, search, shadowMode, v3],
    queryFn: async () => {
      return await accountService.getAllAccounts({ page, limit, search, shadowMode, v3 });
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false
  };
});

// Mutation atom for updating account flags
const updateAccountFlagMutationAtom = atomWithMutation(() => ({
  mutationKey: ['accounts', 'update-flag'],
  mutationFn: ({ accountId, flagKey, flagValue }: { accountId: string; flagKey: string; flagValue: boolean | string | number }) =>
    accountService.updateAccountFlag(accountId, flagKey, flagValue),
  onSuccess: () => {
    invalidateQueries(['accounts']);
  }
}));

// Hook
export function useAccounts() {
  const [accountsQuery] = useAtom(accountsQueryAtom);
  return accountsQuery;
}

export function useAccountsPagination() {
  return useAtom(accountsPaginationAtom);
}

export function useAccountsFilters() {
  return useAtom(accountsFiltersAtom);
}

// Hook for updating account flags
export function useUpdateAccountFlag() {
  const [mutation] = useAtom(updateAccountFlagMutationAtom);
  return mutation;
}
